import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { track } from '@vercel/analytics';
import './FloatingAcademyShopButton.css';

const FloatingAcademyShopButton: React.FC = () => {
  const { language } = useLanguage();
  const [isEnhanced, setIsEnhanced] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    let enhanceTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;

      // Mark that user has scrolled (for slight style changes)
      if (scrollY > 50 && !hasScrolled) {
        setHasScrolled(true);
      }

      // Enhanced animation on longer scrolls for extra attention
      if (scrollY > windowHeight / 2) {
        setIsEnhanced(true);

        // Clear existing timeout
        if (enhanceTimeout) {
          clearTimeout(enhanceTimeout);
        }

        // Remove enhanced state after 3 seconds of no scrolling
        enhanceTimeout = setTimeout(() => {
          setIsEnhanced(false);
        }, 3000);
      } else {
        setIsEnhanced(false);
      }

      // Debounce scroll events
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        // Additional scroll-based logic can go here
      }, 100);
    };

    // Add scroll listener with passive option for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
      if (enhanceTimeout) clearTimeout(enhanceTimeout);
    };
  }, [hasScrolled]);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // Track the click event
    track('academy_shop_button_click', {
      language,
      page_location: window.location.pathname,
      button_position: 'floating_overlay',
      scroll_position: window.scrollY,
      has_scrolled: hasScrolled,
      is_enhanced: isEnhanced,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      screen_width: window.innerWidth
    });

    // Navigate to academy shop
    window.open('https://www.aiaj.xyz/shop', '_blank', 'noopener,noreferrer');
  };

  // Academy/Education icon component
  const AcademyIcon = () => (
    <svg
      className="academy-icon"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
    </svg>
  );

  return (
    <div className={`floating-academy-shop-button visible ${hasScrolled ? 'scrolled' : ''} ${isEnhanced ? 'enhanced' : ''}`}>
      <button
        className="academy-shop-button"
        onClick={handleButtonClick}
        aria-label={language === 'ar' ? 'متجر الأكاديمية - يفتح في نافذة جديدة' : 'Academy Shop - Opens in new window'}
      >
        <AcademyIcon />

        <div className="academy-text">
          <span className="academy-title">
            {language === 'ar' ? 'متجر الأكاديمية' : 'Academy Shop'}
          </span>
          <span className="academy-subtitle">
            {language === 'ar' ? 'أدوات علمية وتعليمية' : 'Educational Tools'}
          </span>
        </div>

        {/* Premium badge for extra attention */}
        <div className="premium-badge"></div>
      </button>
    </div>
  );
};

export default FloatingAcademyShopButton;